import { forwardRef, useMemo } from 'react'
import { Link } from 'react-router'

import Box from '@mui/material/Box'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@components/_mui/Typography'

import { useLayoutState } from '@providers/LayoutStateProvider'

export default function NavItem({ selected = false, disabled = false, url, target = false, external = false, icon, title, action }) {
  const { sidebarCollapsed: mini } = useLayoutState()
  const itemTarget = target ? '_blank' : '_self'
  const sx = styles(mini)

  const internalLink = useMemo(() => forwardRef((itemProps, ref) => <Link to={url} ref={ref} {...itemProps} />), [url])
  const listItemProps = external ? { component: 'a', href: url, target: itemTarget } : { component: internalLink }

  const Icon = icon

  const button = (
    <ListItemButton
      {...listItemProps}
      id={`sidebar-${title}`}
      data-testid={`sidebar-${title}`}
      disabled={disabled}
      selected={selected}
      sx={sx.button}
    >
      {Icon && (
        <ListItemIcon sx={sx.icon}>
          <Icon style={{ fontSize: '1.15rem' }} />
          {mini && action && <Box sx={sx.actionBadge}>{action}</Box>}
        </ListItemIcon>
      )}
      <ListItemText
        sx={sx.text}
        primary={
          <Typography variant="body2" sx={sx.textInner}>
            {title}
          </Typography>
        }
      />
      {!mini && action}
    </ListItemButton>
  )

  return mini ? (
    <Tooltip title={title} placement="right" arrow>
      {button}
    </Tooltip>
  ) : (
    button
  )
}

const TRANSITION = 'all 200ms ease-in-out'

const styles = (mini) => ({
  button: {
    mx: 1,
    mb: '2px',
    px: mini ? 0 : 1,
    py: 1,
    minHeight: 40,
    borderRadius: 1,
    justifyContent: mini ? 'center' : 'flex-start',
    color: 'text.secondary',
    transition: TRANSITION,
    '&:hover': { bgcolor: 'action.hover' },
    '&.Mui-selected': {
      bgcolor: 'action.selected',
      color: 'text.primary',
      fontWeight: 500,
      '&:hover': { bgcolor: 'action.focus' },
    },
  },
  icon: {
    minWidth: mini ? 0 : 28,
    color: 'inherit',
    justifyContent: 'center',
    position: 'relative',
    transition: TRANSITION,
  },
  text: {
    m: 0,
    flex: mini ? 'none' : 1,
    overflow: 'hidden',
    opacity: mini ? 0 : 1,
    maxWidth: mini ? 0 : 'none',
    ml: mini ? 0 : 1.5,
    transition: TRANSITION,
  },
  textInner: {
    fontWeight: 'inherit',
    whiteSpace: 'nowrap',
  },
  actionBadge: {
    position: 'absolute',
    top: -6,
    right: -10,
    transform: 'scale(0.85)',
    transformOrigin: 'top right',
    pointerEvents: 'none',
  },
})
